import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  TablePagination,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import TableToolbar from '../components/TableToolbar';

const WorkerPayData = () => {
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    hourly_rate: '',
    overtime_rate: '',
    payment_method: 'direct_deposit',
    bank_name: '',
    account_number: '',
    routing_number: '',
    tax_id: '',
    start_date: new Date(),
    notes: ''
  });
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const processWorkerData = (data) => {
    return data.map(visit => {
      let distance = 0;
      if (visit.mileage_status === 1) {
        distance = visit.return_mileage === 0 
          ? parseFloat(visit.distance || 0) 
          : parseFloat(visit.distance || 0) * 2;
      }

      const workRate = parseFloat(visit.emp_work_rate || 0);
      const mileageRate = parseFloat(visit.emp_mileage_rate || 0);
      const duration = parseFloat(visit.duration || 0);

      const empDueBal = (distance * mileageRate) + (workRate * duration);

      return {
        ...visit,
        distance,
        empDueBal
      };
    });
  };

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/workers/pay-data');
      
      if (response.data?.data) {
        const processedData = processWorkerData(response.data.data);
        setWorkers(processedData || []);
      } else {
        setWorkers([]);
        enqueueSnackbar('No data received from server', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error fetching worker pay data:', error);
      enqueueSnackbar('Error fetching worker pay data', { variant: 'error' });
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  const handleOpenDialog = (worker = null) => {
    if (worker) {
      setSelectedWorker(worker);
      setFormData({
        hourly_rate: worker.hourly_rate || '',
        overtime_rate: worker.overtime_rate || '',
        payment_method: worker.payment_method || 'direct_deposit',
        bank_name: worker.bank_name || '',
        account_number: worker.account_number || '',
        routing_number: worker.routing_number || '',
        tax_id: worker.tax_id || '',
        start_date: worker.start_date ? new Date(worker.start_date) : new Date(),
        notes: worker.notes || ''
      });
    } else {
      setSelectedWorker(null);
      setFormData({
        hourly_rate: '',
        overtime_rate: '',
        payment_method: 'direct_deposit',
        bank_name: '',
        account_number: '',
        routing_number: '',
        tax_id: '',
        start_date: new Date(),
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedWorker(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedWorker) {
        await api.put(`/workers/${selectedWorker.id}/pay-data`, formData);
        enqueueSnackbar('Worker pay data updated successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchWorkers();
    } catch (error) {
      console.error('Error saving worker pay data:', error);
      enqueueSnackbar('Error saving worker pay data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Convert the current data to CSV format
      const csvData = [];
      
      // Add headers
      const headers = [
        'S.No',
        'Worker Name',
        'Worker Email',
        'Worker Work Rate',
        'Duration',
        'Worker Mileage Rate',
        'Distance',
        'Return Mileage',
        "Worker’s Pay / Worker Pay",
        'Client Name',
        'Client Company Name',
        'Visit Date',
        'Visit Address'
      ];
      csvData.push(headers.join(','));

      // Add data rows
      const dataToExport = searchTerm ? filteredWorkers : workers;
      dataToExport.forEach((worker, index) => {
        const row = [
          index + 1,
          `${worker.emp_first_name} ${worker.emp_last_name}`,
          worker.emp_email,
          worker.emp_work_rate,
          worker.duration,
          worker.emp_mileage_rate,
          worker.distance,
          worker.return_mileage === 0 ? 'No' : 'Yes',
          worker.empDueBal.toFixed(2),
          worker.client_name,
          worker.client_company_name,
          worker.visit_date,
          `"${worker.visit_address?.replace(/"/g, '""')}"`  // Escape quotes in address
        ].join(',');
        csvData.push(row);
      });

      // Create and download CSV file
      const blob = new Blob([csvData.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'worker_pay_data.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      enqueueSnackbar('Export completed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting data:', error);
      enqueueSnackbar('Error exporting data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const filteredWorkers = workers.filter(worker => 
    worker?.emp_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker?.emp_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker?.emp_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker?.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker?.client_company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 3 }, 
      backgroundColor: '#f5f5f5'
    }}>
      <TableToolbar title="Worker Pay Data" />
      
 

      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: { xs: 2, sm: 3 },
        backgroundColor: 'white',
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" color="text.secondary">Show</Typography>
            <TextField
              select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: '80px' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </TextField>
            <Typography variant="body1" color="text.secondary">entries</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" color="text.secondary">Search:</Typography>
            <TextField
              size="small"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search by company name"
              sx={{ width: '250px' }}
            />

            <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={loading || workers.length === 0}
          sx={{
            backgroundColor: '#1a237e',
            '&:hover': {
              backgroundColor: '#0d47a1'
            }
          }}
        >
          Export
        </Button>
      
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <TableContainer sx={{ 
          overflowX: 'auto',
          overflowY: 'hidden',
          flex: 1
        }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#4e0159',
                  color: '#ffffff'
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 0,
                  paddingRight: 1
                }}>S.No</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Worker Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Worker Email</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Work Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Duration</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Mileage Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Distance</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Return Mileage</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Worker’s Pay / Worker Pay</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Company Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Visit Date</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 2
                }}>Visit Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((worker, index) => (
                  <TableRow 
                    key={worker.clientvisitesid || index}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{`${worker.emp_first_name || ''} ${worker.emp_last_name || ''}`}</TableCell>
                    <TableCell>{worker.emp_email || ''}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>${worker.emp_work_rate || '0.00'}</TableCell>
                    <TableCell>{worker.duration || '0'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>${worker.emp_mileage_rate || '0.00'}</TableCell>
                    <TableCell>{worker.distance || '0'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={worker.return_mileage === 0 ? "No" : "Yes"}
                        color={worker.return_mileage === 0 ? "error" : "success"}
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          minWidth: '60px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4e0159' }}>${worker.empDueBal?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>{worker.client_name || ''}</TableCell>
                    <TableCell>{worker.client_company_name || ''}</TableCell>
                    <TableCell>{worker.visit_date ? new Date(worker.visit_date).toLocaleDateString() : ''}</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        maxHeight: '70px', 
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                          width: '6px'
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1',
                          borderRadius: '10px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#888',
                          borderRadius: '10px'
                        }
                      }}>
                        {worker.visit_address || ''}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredWorkers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredWorkers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #e0e0e0',
            flexShrink: 0
          }}
        />
      </Paper>

      {/* Worker Pay Data Form Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {selectedWorker ? `Edit Pay Data - ${selectedWorker.name}` : 'Add Pay Data'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hourly Rate"
                  fullWidth
                  required
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Overtime Rate"
                  fullWidth
                  required
                  type="number"
                  value={formData.overtime_rate}
                  onChange={(e) => setFormData({ ...formData, overtime_rate: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Method"
                  fullWidth
                  required
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  margin="normal"
                >
                  <MenuItem value="direct_deposit">Direct Deposit</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={formData.start_date}
                    onChange={(newValue) => setFormData({ ...formData, start_date: newValue })}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  />
                </LocalizationProvider>
              </Grid>
              {formData.payment_method === 'direct_deposit' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bank Name"
                      fullWidth
                      required
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Account Number"
                      fullWidth
                      required
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Routing Number"
                      fullWidth
                      required
                      value={formData.routing_number}
                      onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tax ID"
                  fullWidth
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 2,
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0'
          }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default WorkerPayData; 